import { useEffect, useState, useCallback } from "react";
import { Bookmark, BookmarkPlus, X, Loader2, Check, Save, AlertCircle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { logDev } from "@/lib/ux";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

// Canonicalize a filter object: drop empty values, sort keys -> stable string for equality.
export function canonFilters(obj) {
  const out = {};
  Object.keys(obj || {}).sort().forEach((k) => {
    const v = obj[k];
    if (v !== "" && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0)) out[k] = v;
  });
  return JSON.stringify(out);
}

export function SavedFilters({ module, filters, onApply, activeCount = 0 }) {
  const [views, setViews] = useState([]);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeId, setActiveId] = useState(null);   // the saved view the user applied
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/saved-filters", { params: { module } });
      setViews(data.data);
    } catch (e) { logDev("Failed to load saved filters", e); }
  }, [module]);
  useEffect(() => { load(); }, [load]);

  const currentKey = canonFilters(filters);
  const activeView = views.find((v) => v.id === activeId);
  // If an applied view's filters still match the current filters, it's "clean".
  const matchesActive = activeView && canonFilters(activeView.filters) === currentKey;
  // Auto-detect when current filters exactly equal some saved view (even without clicking it).
  const exactView = views.find((v) => canonFilters(v.filters) === currentKey && activeCount > 0);
  const dirty = activeView && !matchesActive;

  const applyView = (v) => { setActiveId(v.id); onApply(v.filters || {}); };

  const create = async () => {
    if (!name.trim()) return toast.error("Enter a name for this view");
    setSaving(true);
    try {
      const { data } = await api.post("/saved-filters", { module, name: name.trim(), filters });
      setViews((vs) => [...vs, data.data]);
      setActiveId(data.data.id);
      setName(""); setNaming(false);
      toast.success("View saved");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const updateActive = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/saved-filters/${activeView.id}`, { filters });
      setViews((vs) => vs.map((v) => (v.id === activeView.id ? data.data : v)));
      toast.success(`"${activeView.name}" updated`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    await api.delete(`/saved-filters/${toDelete.id}`);
    setViews((vs) => vs.filter((v) => v.id !== toDelete.id));
    if (activeId === toDelete.id) setActiveId(null);
    toast.success("Saved view deleted");
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`saved-filters-${module}`}>
      <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink/40">
        <Bookmark className="h-3.5 w-3.5" />Saved views
      </span>
      {views.length === 0 && !naming && <span className="text-xs text-ink/40">No saved filters yet</span>}
      {views.map((v) => {
        const isActive = (activeView?.id === v.id && matchesActive) || (!activeView && exactView?.id === v.id);
        return (
          <button
            key={v.id}
            data-testid={`saved-view-${v.id}`}
            onClick={() => applyView(v)}
            className={`group flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${isActive ? "border-loop bg-loop text-white" : "border-sky bg-white text-navy hover:border-loop hover:text-loop"}`}
          >
            {isActive && <Check className="h-3 w-3" />}
            {v.name}
            <X
              role="button"
              data-testid={`saved-view-delete-${v.id}`}
              onClick={(e) => { e.stopPropagation(); setToDelete(v); }}
              className={`h-3.5 w-3.5 rounded-full p-0.5 ${isActive ? "hover:bg-white/20" : "text-ink/30 hover:bg-red-50 hover:text-red-500"}`}
            />
          </button>
        );
      })}

      {/* Unsaved-changes affordances when a saved view was applied then modified */}
      {dirty && (
        <span className="flex items-center gap-2" data-testid="saved-view-dirty">
          <span className="flex items-center gap-1 text-xs font-medium text-amber-600"><AlertCircle className="h-3.5 w-3.5" />Unsaved changes</span>
          <button onClick={updateActive} disabled={saving} data-testid="saved-view-update" className="flex items-center gap-1 rounded-full border border-loop px-2.5 py-1 text-xs font-semibold text-loop hover:bg-loop/5 disabled:opacity-60">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}Update "{activeView.name}"
          </button>
          <button onClick={() => setNaming(true)} data-testid="saved-view-save-as-new" className="rounded-full border border-dashed border-loop px-2.5 py-1 text-xs font-medium text-loop hover:bg-loop/5">Save as new</button>
        </span>
      )}

      {naming ? (
        <div className="flex items-center gap-1">
          <input
            data-testid="saved-view-name-input"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") create(); if (e.key === "Escape") { setNaming(false); setName(""); } }}
            placeholder="View name"
            className="h-8 w-36 rounded-lg border border-sky px-2.5 text-xs outline-none focus:border-loop focus:ring-2 focus:ring-loop/20"
          />
          <button onClick={create} disabled={saving} data-testid="saved-view-confirm" className="flex h-8 items-center gap-1 rounded-lg bg-loop px-2.5 text-xs font-semibold text-white hover:bg-loophover disabled:opacity-60">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
          </button>
          <button onClick={() => { setNaming(false); setName(""); }} className="rounded-lg border border-sky p-1.5 text-ink/50 hover:bg-mist"><X className="h-3.5 w-3.5" /></button>
        </div>
      ) : (
        activeCount > 0 && !exactView && !dirty && (
          <button onClick={() => setNaming(true)} data-testid="save-current-view" className="flex items-center gap-1 rounded-full border border-dashed border-loop px-3 py-1 text-xs font-medium text-loop hover:bg-loop/5">
            <BookmarkPlus className="h-3.5 w-3.5" />Save current
          </button>
        )
      )}

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(v) => !v && setToDelete(null)}
        onConfirm={remove}
        title="Delete saved view?"
        description={toDelete ? `"${toDelete.name}" will be permanently removed. This can't be undone.` : ""}
        confirmLabel="Delete view"
        testid="delete-saved-view-dialog"
      />
    </div>
  );
}
